// External

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from '@ohif/i18n';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, type BrowserRouterProps } from 'react-router-dom';

import Compose from './routes/Mode/Compose';
import {
  ExtensionManager,
  CommandsManager,
  HotkeysManager,
  ServiceProvidersManager,
  SystemContextProvider,
  ViewportRefsProvider,
} from '@ohif/core';
import {
  ThemeWrapper as ThemeWrapperNext,
  NotificationProvider,
  ViewportGridProvider,
  DialogProvider,
  CineProvider,
  TooltipProvider,
  Modal as ModalNext,
  ManagedDialog,
  ModalProvider,
  ViewportDialogProvider,
  UserAuthenticationProvider,
} from '@ohif/ui-next';
// Viewer Project
// TODO: Should this influence study list?
import { AppConfigProvider } from '@state';
import createRoutes from './routes';
import appInit from './appInit.js';
import OpenIdConnectRoutes from './utils/OpenIdConnectRoutes';
import { ShepherdJourneyProvider } from 'react-shepherd';
import './App.css';

let commandsManager: CommandsManager,
  extensionManager: ExtensionManager,
  servicesManager: AppTypes.ServicesManager,
  serviceProvidersManager: ServiceProvidersManager,
  hotkeysManager: HotkeysManager;

const routerFutureFlags: BrowserRouterProps['future'] = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

// ── medicaprof SSO ──────────────────────────────────────────────────────────
// The medicaprof app launches the lab with `?launchToken=<single-use, 60s>`.
// We exchange it at the backend for a real bearer token so the session JWT
// never travels in a URL (history, proxies, access logs). The exchanged token
// lives in sessionStorage so a same-tab refresh keeps working.
const MEDICAPROF_TOKEN_KEY = 'medicaprof.accessToken';

type MedicaprofHandoff = { status: 'none' | 'ready' } | { status: 'pending'; launchToken: string };

function setMedicaprofBearer(token: string, userAuthenticationService): void {
  userAuthenticationService.setServiceImplementation({
    getAuthorizationHeader: () => ({ Authorization: 'Bearer ' + token }),
  });
}

/** Read (and strip) the SSO params from the URL; wire a stored token if any. */
function readMedicaprofHandoff(userAuthenticationService): MedicaprofHandoff {
  try {
    const params = new URLSearchParams(window.location.search);
    const launchToken = params.get('launchToken');
    const legacyToken = params.get('token');

    // Strip auth params from the URL/history immediately, whatever happens next.
    if (launchToken || legacyToken) {
      params.delete('launchToken');
      params.delete('token');
      const search = params.toString();
      const cleanUrl =
        window.location.origin +
        window.location.pathname +
        (search ? '?' + search : '') +
        window.location.hash;
      window.history?.replaceState?.(null, '', cleanUrl);
    }

    if (launchToken) {
      return { status: 'pending', launchToken };
    }

    // DEPRECATED direct-JWT handoff (?token=). Kept only so an older medicaprof
    // frontend keeps working during rollout — remove once the launch-token
    // launcher is deployed everywhere.
    if (legacyToken) {
      setMedicaprofBearer(legacyToken, userAuthenticationService);
      return { status: 'ready' };
    }

    // Same-tab refresh: reuse the token exchanged earlier in this tab.
    const stored = window.sessionStorage?.getItem(MEDICAPROF_TOKEN_KEY);
    if (stored) {
      setMedicaprofBearer(stored, userAuthenticationService);
      return { status: 'ready' };
    }
  } catch (e) {
    console.warn('[medicaprof] SSO handoff failed', e);
  }

  return { status: 'none' };
}

/** POST the single-use launch token to the medicaprof backend for a bearer token. */
async function exchangeMedicaprofLaunchToken(
  launchToken: string,
  apiRoot: string | undefined,
  userAuthenticationService
): Promise<boolean> {
  try {
    if (!apiRoot) {
      throw new Error('medicaprofApiRoot is not configured');
    }
    const res = await fetch(apiRoot + '/lab/launch-token/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ launchToken }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || json?.ok !== 1 || !json.accessToken) {
      throw new Error(`exchange rejected (HTTP ${res.status})`);
    }
    setMedicaprofBearer(json.accessToken, userAuthenticationService);
    try {
      window.sessionStorage?.setItem(MEDICAPROF_TOKEN_KEY, json.accessToken);
    } catch {
      // sessionStorage unavailable → refresh will require a re-launch; fine
    }
    return true;
  } catch (e) {
    console.warn('[medicaprof] launch token exchange failed:', e);
    return false;
  }
}

function App({
  config = {
    /**
     * Relative route from domain root that OHIF instance is installed at.
     * For example:
     *
     * Hosted at: https://ohif.org/where-i-host-the/viewer/
     * Value: `/where-i-host-the/viewer/`
     * */
    routerBasename: '/',
    /**
     *
     */
    showLoadingIndicator: true,
    showStudyList: true,
    oidc: [],
    extensions: [],
  },
  defaultExtensions = [],
  defaultModes = [],
}) {
  const [init, setInit] = useState(null);
  useEffect(() => {
    const run = async () => {
      appInit(config, defaultExtensions, defaultModes).then(setInit).catch(console.error);
    };

    run();
  }, []);

  // medicaprof SSO state. Routes are gated on it below so the worklist QIDO
  // can never fire before the Authorization header is wired — the old code had
  // to set the header synchronously during render for the same reason.
  const [medicaprofAuth, setMedicaprofAuth] = useState<
    'unknown' | 'none' | 'ready' | 'pending' | 'failed'
  >('unknown');
  useEffect(() => {
    if (!init) {
      return;
    }
    const authService = init.servicesManager.services.userAuthenticationService;
    const handoff = readMedicaprofHandoff(authService);
    if (handoff.status === 'pending') {
      setMedicaprofAuth('pending');
      exchangeMedicaprofLaunchToken(
        handoff.launchToken,
        init.appConfig?.medicaprofApiRoot,
        authService
      ).then(ok => setMedicaprofAuth(ok ? 'ready' : 'failed'));
    } else {
      setMedicaprofAuth(handoff.status);
    }
  }, [init]);

  if (!init) {
    return null;
  }

  // Gate rendering until the medicaprof handoff settles: 'none' means this is
  // not a medicaprof launch (other configs/deployments are unaffected).
  if (medicaprofAuth === 'unknown' || medicaprofAuth === 'pending') {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black text-lg text-white">
        Signing in…
      </div>
    );
  }
  if (medicaprofAuth === 'failed') {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-2 bg-black text-white">
        <div className="text-lg">Your session link has expired.</div>
        <div className="text-sm opacity-70">Please open the Imaging Lab from medicaprof again.</div>
      </div>
    );
  }

  // Set above for named export
  commandsManager = init.commandsManager;
  extensionManager = init.extensionManager;
  servicesManager = init.servicesManager;
  serviceProvidersManager = init.serviceProvidersManager;
  hotkeysManager = init.hotkeysManager;

  // Set appConfig
  const appConfigState = init.appConfig;
  const { routerBasename, modes, dataSources, oidc, showStudyList } = appConfigState;

  // get the maximum 3D texture size
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');

  if (gl) {
    const max3DTextureSize = gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
    appConfigState.max3DTextureSize = max3DTextureSize;
  }

  const {
    uiDialogService,
    uiModalService,
    uiViewportDialogService,
    viewportGridService,
    cineService,
    userAuthenticationService,
    uiNotificationService,
    customizationService,
  } = servicesManager.services;

  // medicaprof SSO (launch-token exchange) is handled above, before the gate:
  // by the time routes render here, the Authorization header is already wired
  // for BOTH the worklist QIDO and the viewer's DICOMweb requests.

  const providers = [
    [AppConfigProvider, { value: appConfigState }],
    [UserAuthenticationProvider, { service: userAuthenticationService }],
    [I18nextProvider, { i18n }],
    [ThemeWrapperNext],
    [SystemContextProvider, { commandsManager, extensionManager, hotkeysManager, servicesManager }],
    [ViewportRefsProvider],
    [ViewportGridProvider, { service: viewportGridService }],
    [ViewportDialogProvider, { service: uiViewportDialogService }],
    [CineProvider, { service: cineService }],
    [NotificationProvider, { service: uiNotificationService }],
    [TooltipProvider],
    [DialogProvider, { service: uiDialogService, dialog: ManagedDialog }],
    [ModalProvider, { service: uiModalService, modal: ModalNext }],
    [ShepherdJourneyProvider],
  ];

  // Loop through and register each of the service providers registered with the ServiceProvidersManager.
  const providersFromManager = Object.entries(serviceProvidersManager.providers);
  if (providersFromManager.length > 0) {
    providersFromManager.forEach(([serviceName, provider]) => {
      providers.push([provider, { service: servicesManager.services[serviceName] }]);
    });
  }

  const CombinedProviders = ({ children }) => Compose({ components: providers, children });

  let authRoutes = null;

  // Should there be a generic call to init on the extension manager?
  customizationService.init(extensionManager);

  // Use config to create routes
  const appRoutes = createRoutes({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    routerBasename,
    showStudyList,
  });

  if (oidc) {
    authRoutes = (
      <OpenIdConnectRoutes
        oidc={oidc}
        routerBasename={routerBasename}
        userAuthenticationService={userAuthenticationService}
      />
    );
  }

  return (
    <CombinedProviders>
      <BrowserRouter
        basename={routerBasename}
        future={routerFutureFlags}
      >
        {authRoutes}
        {appRoutes}
      </BrowserRouter>
    </CombinedProviders>
  );
}

App.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
      showLoadingIndicator: PropTypes.bool,
      showStudyList: PropTypes.bool,
      modes: PropTypes.array,
      dataSources: PropTypes.array,
    }),
  ]),
  /* Extensions that are "bundled" or "baked-in" to the application.
   * These would be provided at build time as part of they entry point. */
  defaultExtensions: PropTypes.array,
  /* Modes that are "bundled" or "baked-in" to the application.
   * These would be provided at build time as part of they entry point. */
  defaultModes: PropTypes.array,
};

export default App;

export { commandsManager, extensionManager, servicesManager };
